import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import {
  selectCurrentPatientDailyCheckinData,
  selectExamDateRange,
  selectExamList,
  selectExamMLResultLeftKneeIds,
  selectExamMLResultLeftKneeLabels,
  selectExamMLResultLeftMaxValues,
  selectExamMLResultLeftMinValues,
  selectExamMLResultRightKneeIds,
  selectExamMLResultRightKneeLabels,
  selectExamMLResultRightMaxValues,
  selectExamMLResultRightMinValues,
  selectJointOptions,
  selectPatientRegionDetails,
} from "store/PatientStore/selectors";
import Skeleton from "react-loading-skeleton";
import { RegionBlock } from "../../components/Patient/RegionBlock";
import { selectRequests } from "../../../../store/LoadingStore/selectors";
import { Requests } from "../../../../types/requests";
import RecoveryPlan from "../components/RecoveryPlan";
import { Grid, Typography } from "@mui/material";
import ContralateralSwitch from "../components/ContralateralSwitch";
import LineRegionChart, {
  LineType,
} from "pages/PatientDetails/components/Patient/components/LineRegionChart";
import DoughnutRegionChart from "pages/PatientDetails/components/Patient/components/DoughnutRegionChart";
import PainProgressChart from "pages/PatientDetails/components/Patient/components/PainProgressChart";
import { icons } from "constants/icons";
import moment from "moment";
import { getPatientDailyCheckinData } from "store/PatientStore/actions";
import ProgressSection from "pages/PatientDetails/components/Patient/components/ProgressSection";
import { OptionType } from "pages/PatientDetails/containers/components/PatientRecovery/components/ButtonGroup";
import { RecoveryPlanType } from "types/RecoveryPlan";
import useJointData from "./useRecoveryPlan";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import AddRecoveryPlanPopover from "../components/AddRecoveryPlanPopover";
import StyledPatientRegionDetails from "./StyledPatientRegionDetails.style";
import styles from "../../components/Patient/components/styles";
import { setExamDateRange } from "../../../../store/PatientStore/patientSlice";
import { JointTypeData } from "types/Patient";
import { capitializeAndRemoveUnderScore } from "utils/general";
import { SurveyBlock } from "pages/PatientDetails/components/Patient/SurveyBlock";
import LineSurveyChart from "pages/PatientDetails/components/Patient/components/LineSurveyChart";
import { ExamType } from "constants/types";

export const PatientRegionDetails = ({
  patientId,
  isFromCronJob,
}: {
  patientId: any;
  isFromCronJob?: boolean;
}) => {
  const patientRegionDetails = useAppSelector(selectPatientRegionDetails);
  const loading = useAppSelector((state) =>
    selectRequests([Requests.getPatientRegionDetails], state)
  );
  const leftKneeIds = useAppSelector(selectExamMLResultLeftKneeIds);
  const rightKneeIds = useAppSelector(selectExamMLResultRightKneeIds);
  const leftKneeLabels = useAppSelector(selectExamMLResultLeftKneeLabels);
  const rightKneeLabels = useAppSelector(selectExamMLResultRightKneeLabels);
  const leftMaxValues = useAppSelector(selectExamMLResultLeftMaxValues);
  const rightMaxValues = useAppSelector(selectExamMLResultRightMaxValues);
  const leftMinValues = useAppSelector(selectExamMLResultLeftMinValues);
  const rightMinValues = useAppSelector(selectExamMLResultRightMinValues);
  const patientDailyCheckin = useAppSelector(
    selectCurrentPatientDailyCheckinData
  );
  const examDateRange = useAppSelector(selectExamDateRange);
  const examList = useAppSelector(selectExamList);
  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const [contralateralStates, setContralateralStates] = useState(
    Array(patientRegionDetails.length).fill(false)
  );
  const jointOptions = useAppSelector(selectJointOptions);
  const [iconElement, setIconElement] = React.useState<Element | null>(null);
  const dispatch = useAppDispatch();
  const { isJointDataLoading, jointData, setJointData } = useJointData({
    patientId,
    isFromCronJob,
  });
  const handleContralateralToggle = (index: any, isChecked: boolean) => {
    const newContralateralStates = [...contralateralStates];
    newContralateralStates[index] = isChecked;
    setContralateralStates(newContralateralStates);
  };

  const closePopover = () => {
    setIconElement(null);
  };

  useEffect(() => {
    const x = new Date();
    x.setMonth(selectedMonth);
    dispatch(getPatientDailyCheckinData(patientId, x));
  }, [selectedMonth]);

  const filteredJointOptions = React.useMemo(() => {
    let jointKneeOptions =
      jointOptions?.filter(
        (option) =>
          option.label === "Right Knee" || option.label === "Left Knee"
      ) || [];

    if (jointData?.leftJoint.recoveryPlan) {
      jointKneeOptions = jointKneeOptions.filter((option) => {
        return option.label !== "Left Knee";
      });
    }

    if (jointData?.rightJoint.recoveryPlan) {
      jointKneeOptions = jointKneeOptions.filter((option) => {
        return option.label !== "Right Knee";
      });
    }
    return jointKneeOptions;
  }, [jointOptions, jointData]);

  const handleJointClick = (option: OptionType) => {
    setJointData((prev) => {
      const jointSide =
        option.label === "Right Knee" ? "rightJoint" : "leftJoint";
      if (!prev?.[jointSide]) return prev;
      const [side, name] = option.label.split(" ");
      return {
        ...prev,
        [jointSide]: {
          ...prev[jointSide],
          recoveryPlan: {
            joint: {
              _id: option.value,
              side: side.toLowerCase(),
              name: name.toLowerCase(),
            },
          } as RecoveryPlanType,
        },
      };
    });
    setIconElement(null);
  };

  const updateRecoveryPlan = (recoveryPlan?: RecoveryPlanType) => {
    if (!recoveryPlan) {
      return;
    }

    setJointData((prev) => {
      const side = recoveryPlan?.joint?.side;
      const selectedSide = `${side}Joint` as "leftJoint" | "rightJoint";
      const prevSide = prev?.[selectedSide];
      if (!prevSide) {
        return prev;
      }
      return {
        ...prev,
        [selectedSide]: {
          ...prevSide,
          recoveryPlan,
        },
      };
    });
  };

  const getJointSide = (jointData: JointTypeData) => {
    let side = jointData.examData?.region?.joint?.side;
    if (!jointData.examData) {
      side = jointData.recoveryPlan?.joint?.side || "";
    }
    return side;
  };

  const surveyData = useMemo(() => {
    const list = examList || [];
    const totalExams = list.length;
    const completedSurveyList = list?.filter(
      (exam: ExamType) => exam.outcomeScore
    );

    return {
      totalExams,
      completedSurveyList,
    };
  }, [examList]);

  return (
    <StyledPatientRegionDetails>
      {loading || isJointDataLoading ? (
        <div className="flex flex-col mt-5 space-y-5">
          <Skeleton height={300} />
          <Skeleton height={300} />
        </div>
      ) : (
        <div className="mb-5">
          <div>
            {Object.values(jointData || {}).map((singleJointData, index) => {
              return (
                <>
                  {(singleJointData?.recoveryPlan || isFromCronJob) && (
                    <div className="mb-5">
                      {!isFromCronJob && (
                        <ContralateralSwitch
                          onToggle={(isChecked: boolean) =>
                            handleContralateralToggle(index, isChecked)
                          }
                        />
                      )}

                      <div className="flex flex-col card w-full p-8 rounded-[20px]">
                        <Grid container spacing={2}>
                          {!isFromCronJob && singleJointData.recoveryPlan && (
                            <Grid xs={4}>
                              <RegionBlock
                                lastExam={
                                  singleJointData.examData?.lastExamDate ?? ""
                                }
                                title={
                                  singleJointData.recoveryPlan.joint?.name ?? ""
                                }
                                subtitle={
                                  singleJointData.recoveryPlan.joint?.side
                                }
                                week={singleJointData.examData?.week ?? 0}
                                painScale={
                                  singleJointData.examData?.painScale ?? 0
                                }
                                score={
                                  singleJointData.examData?.outcomeScore ?? "-"
                                }
                                patientLastExamDate={
                                  singleJointData.examData?.lastExamDate
                                }
                                showRegionInfo
                              />
                              {!singleJointData.examData && (
                                <p> No exam data available</p>
                              )}
                            </Grid>
                          )}
                          {isFromCronJob && (
                            <Grid xs={12}>
                              <Typography variant="h5" sx={{ mb: 5 }}>
                                {capitializeAndRemoveUnderScore(
                                  getJointSide(singleJointData)
                                )}{" "}
                                Knee values
                              </Typography>
                            </Grid>
                          )}
                          <Grid
                            container
                            xs={isFromCronJob ? 12 : 8}
                            sx={{ alignContent: "flex-end" }}
                          >
                            <Grid xs={6} sx={{ height: "100%" }}>
                              <div
                                id={`${getJointSide(singleJointData)}-side`}
                              />
                              <LineRegionChart
                                ids={
                                  getJointSide(singleJointData) === "left"
                                    ? leftKneeIds
                                    : rightKneeIds
                                }
                                labels={
                                  getJointSide(singleJointData) === "left"
                                    ? leftKneeLabels
                                    : rightKneeLabels
                                }
                                data={
                                  getJointSide(singleJointData) === "left"
                                    ? leftMinValues
                                    : rightMinValues
                                }
                                opositeChartData={
                                  getJointSide(singleJointData) === "left"
                                    ? leftMaxValues
                                    : rightMaxValues
                                }
                                contralateralData={
                                  getJointSide(singleJointData) === "left"
                                    ? rightMinValues
                                    : leftMinValues
                                }
                                contralateralLabels={
                                  getJointSide(singleJointData) === "left"
                                    ? rightKneeLabels
                                    : leftKneeLabels
                                }
                                graphType={LineType.Extension}
                                isContralateral={contralateralStates[index]}
                              />
                            </Grid>
                            <Grid xs={6} sx={{ height: "100%" }}>
                              <LineRegionChart
                                ids={
                                  getJointSide(singleJointData) === "left"
                                    ? leftKneeIds
                                    : rightKneeIds
                                }
                                labels={
                                  getJointSide(singleJointData) === "left"
                                    ? leftKneeLabels
                                    : rightKneeLabels
                                }
                                data={
                                  getJointSide(singleJointData) === "left"
                                    ? leftMaxValues
                                    : rightMaxValues
                                }
                                opositeChartData={
                                  getJointSide(singleJointData) === "left"
                                    ? leftMinValues
                                    : rightMinValues
                                }
                                contralateralData={
                                  getJointSide(singleJointData) === "left"
                                    ? rightMaxValues
                                    : leftMaxValues
                                }
                                contralateralLabels={
                                  getJointSide(singleJointData) === "left"
                                    ? rightKneeLabels
                                    : leftKneeLabels
                                }
                                graphType={LineType.Flexion}
                                isContralateral={contralateralStates[index]}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        {!isFromCronJob && (
                          <div className="text-right space-x-1 mt-2.5">
                            <Button
                              variant={"contained"}
                              style={
                                examDateRange === "all"
                                  ? styles.selectedButtonBottom
                                  : styles.buttonBottom
                              }
                              onClick={() => dispatch(setExamDateRange("all"))}
                            >
                              All
                            </Button>
                            <Button
                              variant={"contained"}
                              style={
                                examDateRange === "month"
                                  ? styles.selectedButtonBottom
                                  : styles.buttonBottom
                              }
                              onClick={() =>
                                dispatch(setExamDateRange("month"))
                              }
                            >
                              Month
                            </Button>
                            <Button
                              variant={"contained"}
                              style={
                                examDateRange === "week"
                                  ? styles.selectedButtonBottom
                                  : styles.buttonBottom
                              }
                              onClick={() => dispatch(setExamDateRange("week"))}
                            >
                              Week
                            </Button>
                            <Button
                              variant={"contained"}
                              style={
                                examDateRange === "year"
                                  ? styles.selectedButtonBottom
                                  : styles.buttonBottom
                              }
                              onClick={() => dispatch(setExamDateRange("year"))}
                            >
                              Year
                            </Button>
                          </div>
                        )}

                        {singleJointData?.recoveryPlan && !isFromCronJob && (
                          <Grid xs={12} sx={{ marginTop: 3 }}>
                            <RecoveryPlan
                              setJointData={setJointData}
                              jointData={jointData}
                              recoveryPlan={singleJointData?.recoveryPlan}
                              patientId={patientId}
                              updateRecoveryPlanData={updateRecoveryPlan}
                            />
                          </Grid>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
            })}

            {!isFromCronJob && (
              <Box mb={2}>
                <Button
                  variant="contained"
                  className="recovery-plan-button"
                  onClick={(ev) => setIconElement(ev.target as Element)}
                >
                  Add recovery plan
                </Button>

                <AddRecoveryPlanPopover
                  anchorEl={iconElement}
                  onClose={closePopover}
                  onClick={handleJointClick}
                  jointOptions={filteredJointOptions}
                />
              </Box>
            )}
          </div>

          {isFromCronJob && (
            <div className="flex flex-col card w-full p-8 rounded-[20px]">
              <Grid container spacing={2}>
                <Grid xs={4}>
                  <SurveyBlock
                    title={"Survey & Questionnarie Results"}
                    totalExams={surveyData.totalExams}
                    completedSurveys={surveyData.completedSurveyList.length}
                  />
                </Grid>

                <Grid xs={8} sx={{ height: "100%" }}>
                  <LineSurveyChart
                    yData={surveyData.completedSurveyList.map(
                      (exam: ExamType) => exam.createdAt
                    )}
                    xData={surveyData.completedSurveyList.map(
                      (exam: ExamType) => exam.outcomeScore
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          )}

          <div className="flex card w-full p-5 rounded-[20px]">
            <Grid container xs={5}>
              <Grid xs={12}>
                <div
                  id="dailycheck-ins"
                  className="flex space-x-2 items-center"
                >
                  <img src={icons.patientShoulder} alt="status" />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 27,
                      color: "#161728",
                      paddingTop: 0.5,
                    }}
                  >
                    Daily Checkins
                  </Typography>
                </div>
              </Grid>
              <Grid xs={12}>
                <DoughnutRegionChart />
              </Grid>
            </Grid>
            <Grid container xs={7} sx={{ height: 320 }}>
              <ProgressSection
                isFromCronJob={isFromCronJob}
                totalCheckin={
                  patientDailyCheckin?.data?.recommendationProgressData
                    ?.totalCheckIns ?? 0
                }
                completedCheckins={
                  (patientDailyCheckin?.data?.recommendationProgressData
                    ?.totalCheckIns ?? 0) -
                  (patientDailyCheckin?.data?.recommendationProgressData
                    ?.skip ?? 0)
                }
                updateSelectedMonth={setSelectedMonth}
              />
              <PainProgressChart
                isFromCronJob={isFromCronJob}
                selectedMonth={selectedMonth}
              />
            </Grid>
          </div>
        </div>
      )}
    </StyledPatientRegionDetails>
  );
};
